// English translations
const en = {
  common: {
    appName: "Protego",
    subtitle: "Global compliance verification tool",
    footer: "All rights reserved",
    search: "Search",
    profile: "Profile",
    admin: "Admin",
    loading: "Loading...",
    welcome: "Welcome",
    error: "Error",
    success: "Success",
    save: "Save",
    cancel: "Cancel",
    edit: "Edit",
    delete: "Delete",
    close: "Close",
    back: "Back",
    next: "Next",
    submit: "Submit",
    download: "Download",
    monitor: "Monitor",
    confirm: "Confirm",
    processing: "Processing...",
    completed: "Completed",
    generating: "Generating...",
    retry: "Retry",
    unknown: "Unknown",
    unknownUser: "Unknown user",
    copyLink: "Copy Link",
    copied: "Copied!",
    backToSearch: "Back to Search",
    backToCases: "Back to Cases",
    backToReport: "Back to Report",
    backToHistory: "Back to History",
    backToMonitoring: "Back to Monitoring",
    yes: "Yes",
    no: "No",
    name: "Name",
    organization: "Organization",
    active: "Active",
    saving: "Saving..."
  },
  welcomeSection: {
    advancedSearch: {
      title: "Advanced Search",
      description: "Intelligent name-matching algorithms recognize variations in spelling and structure."
    },
    confidenceScoring: {
      title: "Confidence Scoring",
      description: "Results are ranked with detailed confidence levels to identify relevant matches."
    },
    complianceReady: {
      title: "Compliance Ready",
      description: "Generate screening reports with unique IDs for regulatory compliance."
    }
  },
  search: {
    placeholder: "Enter a name to check against sanctions lists...",
    button: "Search",
    advancedOptions: "Advanced Options",
    noResults: "No results found",
    resultsCount: "{{count}} results found",
    entityType: "Entity Type",
    personType: "People",
    enterpriseType: "Companies",
    confidence: {
      veryHigh: "Very High",
      high: "High",
      medium: "Medium",
      low: "Low"
    },
    searching: "Searching sanctions lists...",
    loading: "Loading...",
    scanningRecords: "Scanning database records",
    processed: "Processed",
    of: "of",
    records: "records",
    searchResults: "Search Results",
    foundMatches: "Found {{count}} matches for \"{{term}}\"",
    showingTop: "Showing top {{shown}} matches out of {{total}} (sorted by relevance)",
    all: "All",
    riskMatch: "Risk Match",
    veryHighRiskMatch: "Very High Risk Match",
    highRiskMatch: "High Risk Match",
    mediumRiskMatch: "Medium Risk Match",
    lowRiskMatch: "Low Risk Match",
    filters: {
      all: "All",
      veryHigh: "Very High",
      high: "High",
      medium: "Medium",
      low: "Low"
    },
    includeDismissed: "Include dismissed",
    searchId: "Search ID",
    caseStatus: "Case Status",
    caseStatusPending: "Pending Review",
    caseStatusPartial: "Partially Reviewed",
    caseStatusCompleted: "Review Complete",
    searchTerm: "Search Term",
    caseId: "Case ID",
    caseCreated: "Case Created",
    totalResults: "Total Results Found",
    name: "Name",
    companyName: "Company Name"
  },
  results: {
    noSanctionsFound: "No Sanctions Found",
    cleared: "does not appear on any sanctions lists. This individual has been cleared against all monitored sanctions databases.",
    clearedEntity: "does not appear on any sanctions lists. This entity has been cleared against all monitored sanctions databases.",
    individualCleared: "This individual has been cleared against all monitored sanctions databases.",
    companyCleared: "This company has been cleared against all monitored sanctions databases.",
    verificationId: "Verification ID",
    recordsScanned: "Records Scanned",
    searchTimestamp: "Search Timestamp",
    downloadPdfReport: "Download PDF Report",
    addToMonitoring: "Add to Monitoring",
    removeFromMonitoring: "Remove from Monitoring",
    sourcesChecked: "Sources Checked",
    financialSanctions: "Financial Sanctions",
    travelBans: "Travel Bans",
    unSanctions: "UN Sanctions",
    databaseChecked: "Database checked",
    disclaimer: "This report is system-generated and serves as documentation of the screening process. Regular rescreening is recommended to ensure compliance with current sanctions regulations.",
    legalNote: "For legal compliance purposes, please retain a copy of this report.",
    generatingPdf: "Generating PDF Report...",
    generatingPdfReport: "Generating PDF Report",
    searchDate: "Search Date",
    validUntil: "Valid Until",
    recordsScanned: "Records Scanned",
    authoritiesChecked: "Authorities Checked",
    verification: "Verification",
    issuedBy: "Issued by",
    issuedByValue: "Protego",
    verifyAt: "Verify at",
    verificationDisclaimer: "This report is a point-in-time verification and does not guarantee future compliance status.",
    generatedOn: "This report was generated on",
    searchingTitle: "Searching Sanctions Database",
    searching: "Searching...",
    searchingFirstStage: "Identifying potential matches...",
    searchingVectorStage: "Searching vector database...",
    preparingAnalysis: "Preparing detailed analysis...",
    searchingSecondStage: "Analyzing potential matches...",
    searchProgress: "Processed {{processed}} of {{total}} records",
    firstStageProgress: "Initial scan: {{processed}} of {{total}} records",
    secondStageProgress: "Detailed analysis: {{processed}} of {{total}} candidates",
    verificationId: "Verification ID",
    function: "Function",
    fuzzy: "fuzzy",
    viewOnEUSanctionsTracker: "View on EU Sanctions Tracker",
    viewOnUNSecurityCouncilSanctionsList: "View on UN Security Council Sanctions List",
    scoreBreakdown: "Score Breakdown",
    tokenMatch: "Token Match Score",
    positional: "Positional Score",
    transliteration: "Transliteration Score",
    distributedMatchBoost: "Distributed Match Boost",
    culturalPattern: "Cultural Pattern Detection",
    distributedNameMatch: "Cross-Name Match",
    crossMatchExplanation: "Search text \"{{term}}\" was matched across different name variations. The search term was distributed across these names:",
    matchedVariants: "Matched Variants",
    sanctionsDetails: "Sanctions Details",
    category: "Category",
    sanctionType: "Sanction Type",
    issueDate: "Issue Date",
    country: "Country",
    program: "Program",
    moreDetails: "More Details",
    listingDate: "Listing Date",
    lastUpdated: "Last Updated",
    documents: "Documents",
    sanctionsReport: "Sanctions Report",
    clearanceReport: "Clearance Report",
    expiry: "Valid Until",
    reportTimestamp: "Report Generated",
    citizenship: "Citizenship",
    alternativeNames: "Alternative Names",
    documentId: "Document ID",
    dismiss: "Dismiss",
    confirmMatch: "Confirm Match",
    confirmed: "Confirmed",
    dismissed: "Dismissed",
    confirmDismiss: "Dismiss as False Positive?",
    dismissExplanation: "This will mark the result as dismissed and record it as a false positive in your case management system.",
    undoReview: "Undo review decision",
    match: "Match",
    source: "Source",
    type: "Type",
    score: "Score",
    reviewedOn: "Reviewed on",
    reviewNotes: "Review Notes",
    dismissedMatches: "Dismissed Potential Matches",
    confirmedMatches: "Confirmed Sanctions Matches",
    reviewDate: "Review Date",
    matchesFoundSummary: "This screening has identified <strong>{{count}}</strong> confirmed sanctions match{{plural}}. Additional due diligence is required.",
    allMatchesDismissed: "All {{count}} potential match{{plural}} reviewed and dismissed. No sanctions matches were confirmed."
  },
  report: {
    title: "Screening Report",
    subjectInformation: "Subject Information",
    name: "Name",
    generated: "Report Generated",
    screeningDetails: "Screening Details",
    searchDate: "Search Date",
    validUntil: "Valid Until",
    recordsScanned: "Records Scanned",
    authoritiesChecked: "Authorities Checked",
    sourcesChecked: "Sources Checked",
    verification: "Verification",
    issuedBy: "Issued by",
    issuedByValue: "Protego",
    verifyAt: "Verify at",
    verificationDisclaimer: "This report is a point-in-time verification and does not guarantee future compliance status.",
    generatedOn: "Generated on",
    verificationId: "Verification ID",
    runBy: "Run by",
    downloadButton: "Download Report",
    tooltip: "The case report can only be downloaded once all potential matches have been reviewed.",
    loading: "Generating report...",
    error: "Error generating report",
    caseCompleted: "Case Complete",
    casePending: "Case must be fully reviewed to download report",
    matchesConfirmed: "Sanctions Matches Confirmed",
    noMatchesConfirmed: "No Sanctions Matches",
    reviewedBy: "Reviewed by"
  },
  authorities: {
    euCommission: "European Commission",
    euCommissionDescription: "Financial Sanctions, Travel Bans",
    unitedNations: "United Nations",
    unitedNationsDescription: "Security Council Sanctions"
  },
  auth: {
    login: "Login",
    signup: "Sign Up",
    logout: "Logout",
    welcomeBack: "Welcome Back",
    email: "Email",
    password: "Password",
    forgotPassword: "Forgot Password?",
    noAccount: "Don't have an account?",
    alreadyHaveAccount: "Already have an account?",
    createAccount: "Create Account",
    loginNow: "Sign in to continue to the Sanctions Screener",
    resetPassword: "Reset Password",
    resetPasswordInstructions: "Enter your email address and we'll send you a link to reset your password.",
    resetPasswordPrompt: "Enter your email to receive a password reset link",
    sendResetLink: "Send Reset Link",
    resetLinkSent: "Reset link sent to your email.",
    checkEmail: "Please check your email.",
    backToLogin: "Back to Login",
    dontHaveAccount: "Don't have an account?",
    enterEmail: "Enter your email",
    enterPassword: "Enter your password",
    enterName: "Enter your name",
    confirmPassword: "Confirm Password",
    confirmYourPassword: "Confirm your password",
    passwordsDoNotMatch: "Passwords do not match",
    accountCreated: "Account created successfully!",
    accountCreatedSuccessfully: "Account created successfully! You are now logged in.",
    passwordResetEmailSent: "Password reset email sent! Check your inbox.",
    loginError: "Login failed. Please check your credentials.",
    signupError: "Account creation failed. Email may already be in use.",
    resetError: "Failed to send reset email. Please check the email address.",
    switchToLightMode: "Switch to Light Mode",
    switchToDarkMode: "Switch to Dark Mode",
    name: "Name",
    sessionExpired: "Your session has expired. Please sign in again.",
    or: "or",
    continueWithGoogle: "Continue with Google",
    continueWithMicrosoft: "Sign in with Microsoft",
    emailVerificationRequired: "Please verify your email before continuing. Check your inbox for a verification link.",
    verificationEmailSent: "A verification email has been sent to your email address. Please check your inbox and verify your email before continuing.",
    verificationEmailResent: "Verification email has been resent. Please check your inbox.",
    verificationRequired: "Please verify your email address to continue.",
    resendVerification: "Resend verification email",
    emailVerified: "Email verified successfully!",
    verifyYourEmail: "Verify Your Email",
    verificationInstructions: "We have sent a verification email to your email address. Please check your inbox and click the verification link to activate your account.",
    iAlreadyVerified: "I already verified my email",
    emailNotVerified: "Your email is not verified yet. Please check your inbox.",
    resendVerificationError: "Failed to resend verification email. Please try again later.",
    resendIn: "Resend in",
    resendCooldown: "Please wait before requesting another email.",
    loginRequired: "Please log in to access this page.",
    verificationPageTitle: "Email Verification Required"
  },
  user: {
    profile: "Profile",
    settings: "Settings",
    account: "Account",
    history: "History",
    language: "Language",
    theme: "Theme",
    notifications: "Notifications",
    security: "Security",
    personalInfo: "Personal Information",
    changePassword: "Change Password",
    twoFactor: "Two-Factor Authentication",
    firstName: "First Name",
    lastName: "Last Name",
    company: "Company",
    position: "Position",
    phoneNumber: "Phone Number",
    address: "Address",
    update: "Update Profile",
    profileUpdated: "Profile updated successfully.",
    oldPassword: "Old Password",
    newPassword: "New Password",
    confirmPassword: "Confirm Password",
    passwordChanged: "Password changed successfully."
  },
  orgAdmin: {
    title: "Organization Administration",
    subtitle: "Manage your organization: {{orgName}}",
    accessDenied: "Access Denied",
    accessDeniedMessage: "You don't have permission to access the organization admin page. Only organization administrators can view this content.",
    tabs: {
      userManagement: "User Management",
      organizationSettings: "Organization Settings",
      billingSubscription: "Billing & Subscription"
    },
    userManagement: {
      title: "Organization Members",
      subtitle: "Manage users in your organization",
      inviteUser: "Invite User",
      inviteNewUser: "Invite New User",
      emailAddress: "Email Address",
      emailPlaceholder: "user@example.com",
      role: "Role",
      roles: {
        member: "Member",
        admin: "Admin"
      },
      sendInvitation: "Send Invitation",
      pendingInvitations: "Pending Invitations",
      inviteCode: "Invite Code",
      cancelInvitation: "Cancel invitation",
      activeMembers: "Active Members",
      user: "User",
      joined: "Joined",
      actions: "Actions",
      unknown: "Unknown",
      confirmRemoveUser: "Are you sure you want to remove this user from the organization?",
      userAlreadyExists: "User is already part of this organization",
      emailRequired: "Email is required",
      inviteUserFailed: "Failed to invite user. Please try again.",
      updateRoleFailed: "Failed to update user role",
      cancelInviteFailed: "Failed to cancel invitation",
      removeUserFailed: "Failed to remove user",
      loadUsersFailed: "Failed to load users",
      teamMemberLimit: {
        exceeded: "Team member limit exceeded",
        warning: "You have {{current}}/{{limit}} team members",
        cannotJoin: "This organization has reached its maximum team member limit",
        cannotInvite: "Cannot invite more team members. Upgrade your plan to add more.",
        currentCount: "{{current}}/{{limit}} Team Members",
        upgradeRequired: "Upgrade your plan to add more team members",
        atLimit: "You have reached your team member limit",
        nearLimit: "You are approaching your team member limit"
      }
    },
    organizationSettings: {
      title: "Organization Settings",
      subtitle: "Configure your organization's global settings",
      organizationDetails: "Organization Details",
      organizationName: "Organization Name",
      domain: "Domain",
      domainCannotChange: "Domain cannot be changed after organization creation",
      defaultSearchSettings: "Default Search Settings",
      defaultSearchCadence: "Default Search Cadence",
      defaultCadenceHelp: "Default cadence for new users",
      timezone: "Timezone",
      cadenceOptions: {
        "1year": "1 Year",
        "6months": "6 Months",
        "3months": "3 Months",
        "1month": "1 Month"
      },
      timezoneOptions: {
        "UTC": "UTC",
        "America/New_York": "Eastern Time",
        "America/Chicago": "Central Time",
        "America/Denver": "Mountain Time",
        "America/Los_Angeles": "Pacific Time",
        "Europe/London": "London",
        "Europe/Paris": "Paris",
        "Europe/Berlin": "Berlin",
        "Asia/Tokyo": "Tokyo",
        "Asia/Shanghai": "Shanghai"
      },
      saveChanges: "Save Changes",
      settingsUpdated: "Settings updated successfully",
      settingsUpdateFailed: "Failed to update settings. Please try again."
    },
    billing: {
      title: "Billing & Subscription",
      subtitle: "Manage your organization's subscription and billing information",
      trialActive: "Trial Period Active",
      trialDaysRemaining: "{{days}} days remaining in your trial",
      trialExpired: "Your trial has expired",
      upgradeNow: "Upgrade Now",
      currentSubscription: "Current Subscription",
      plan: "Plan",
      status: "Status",
      nextBillingDate: "Next Billing Date",
      unknownPlan: "Unknown Plan",
      paymentRequired: "Payment Required",
      paymentOverdue: "Your subscription payment is overdue. Please update your payment method.",
      noActiveSubscription: "No Active Subscription",
      noSubscriptionMessage: "Your organization doesn't have an active subscription yet.",
      organizationUsage: "Organization Usage",
      activeMembers: "Active Members",
      searchesThisMonth: "Searches This Month",
      activeCases: "Active Cases",
      apiCallsThisMonth: "API Calls This Month",
      billingActions: "Billing Actions",
      manageSubscription: "Manage Subscription",
      manageSubscriptionDesc: "Update payment method, view invoices, change plan",
      notAvailableDuringTrial: "Not available during trial period",
      opening: "Opening...",
      usageAnalytics: "Usage Analytics",
      usageAnalyticsDesc: "Detailed usage reports and analytics (Coming Soon)",
      needHelp: "Need Help?",
      needHelpMessage: "If you have questions about billing or need to make changes to your subscription, our support team is here to help.",
      contactSupport: "Contact Support →"
    }
  },
  profile: {
    title: "User Profile",
    personalInfo: "Personal Information",
    accountSettings: "Account Settings",
    usageHistory: "Usage History",
    savedSearches: "Saved Searches",
    monitoredEntities: "Monitored Entities",
    apiKeys: "API Keys",
    updateProfile: "Update Profile",
    changePassword: "Change Password",
    deleteAccount: "Delete Account",
    confirmDelete: "Are you sure you want to delete your account? This action cannot be undone.",
    currentPassword: "Current Password",
    newPassword: "New Password",
    confirmNewPassword: "Confirm New Password",
    passwordUpdated: "Password successfully updated",
    profileUpdated: "Profile successfully updated",
    searchHistory: "Search History",
    recentSearches: "Recent Searches",
    noSearchesYet: "No searches yet",
    searchCount: "Search Count",
    lastSearch: "Last Search",
    accountCreated: "Account Created",
    lastSignIn: "Last Sign In",
    subscription: "Subscription",
    status: "Status",
    active: "Active",
    paymentOverdue: "Payment Overdue",
    tier: "Tier",
    professional: "Professional",
    starter: "Starter",
    noSubscription: "You don't have an active subscription.",
    manageSubscription: "Manage Subscription",
    subscribeNow: "Subscribe Now",
    organization: "Organization",
    role: "Role",
    admin: "Organization Admin",
    member: "Member",
    organizationSubscription: "Organization Subscription",
    noOrganizationSubscription: "Your organization doesn't have an active subscription.",
    contactAdmin: "Contact your organization admin to manage billing and subscriptions.",
    nextBilling: "Next Billing",
    marketingPreferences: "Marketing Preferences",
    sanctionsUpdates: "Sanctions & Compliance Updates",
    marketingDescription: "Receive priority alerts about critical sanctions updates, regulatory changes, and new compliance features that could impact your business.",
    marketingOn: "You will receive marketing communications",
    marketingOff: "You will not receive marketing communications",
    marketingEnabled: "Marketing notifications enabled",
    marketingDisabled: "Marketing notifications disabled"
  },
  billing: {
    adminRequired: "Organization Admin Access Required"
  },
  payment: {
    success: {
      title: "Thank You for Subscribing!",
      subtitle: "Welcome to enhanced sanctions screening",
      planActive: "Your subscription is now active",
      features: "Your plan includes:",
      nextRenewal: "Next renewal",
      stayUpdated: "Stay Ahead of Sanctions Changes",
      marketingText: "Get priority alerts on critical sanctions updates, regulatory changes, and new compliance features that could impact your business.",
      marketingOptIn: "Yes, keep me informed about important sanctions and compliance updates",
      continue: "Continue to Dashboard",
      footerNote: "You can manage your subscription and preferences anytime in your account settings."
    }
  },
  admin: {
    dashboard: "Dashboard",
    users: "Users",
    searchHistory: "Search History",
    monitoring: "Monitoring",
    settings: "Settings",
    systemStatus: "System Status",
    viewLogs: "View Logs",
    updateDatabase: "Update Database",
    addUser: "Add User",
    editUser: "Edit User",
    deleteUser: "Delete User",
    statistics: "Statistics",
    apiKeys: "API Keys",
    integrations: "Integrations",
    createApiKey: "Create API Key",
    revokeApiKey: "Revoke API Key",
    databaseStats: "Database Statistics",
    lastUpdate: "Last Update",
    totalRecords: "Total Records",
    searchesPerDay: "Searches Per Day",
    activeUsers: "Active Users",
    monitoring: "Monitoring",
    totalMonitored: "Total Monitored Entities",
    alerts: "Alerts",
    addToMonitoring: "Add to Monitoring",
    removeFromMonitoring: "Remove from Monitoring",
    monitoringFrequency: "Monitoring Frequency",
    daily: "Daily",
    weekly: "Weekly",
    monthly: "Monthly"
  },
  errors: {
    general: "Something went wrong. Please try again.",
    login: "Invalid email or password.",
    required: "This field is required.",
    invalidEmail: "Please enter a valid email address.",
    passwordMatch: "Passwords do not match.",
    passwordLength: "Password must be at least 8 characters long.",
    passwordRequirements: "Password requirements:",
    passwordMinLength: "10+ characters",
    passwordUppercase: "1+ uppercase letter",
    passwordLowercase: "1+ lowercase letter",
    passwordNumber: "1+ number",
    passwordSpecial: "1+ special character",
    serverError: "Server error. Please try again later.",
    notFound: "Page not found",
    unauthorized: "You are not authorized to perform this action.",
    forbidden: "Access forbidden.",
    offline: "You appear to be offline. Please check your connection.",
    timeout: "Request timed out. Please try again.",
    conflict: "This operation caused a conflict. Please try again.",
    emptyResult: "No results found. Please try a different search term.",
    validation: "Please correct the errors in the form.",
    jobNotFound: "The requested job was not found.",
    jobAccessDenied: "You do not have permission to access this job.",
    noVerificationId: "No verification ID available for this match.",
    pdfGenerationFailed: "There was an error generating the PDF report.",
    downloadFailed: "Failed to download results. Please try again.",
    googleAuthFailed: "Google authentication failed. Please try again.",
    popupClosed: "Authentication window was closed. Please try again.",
    accountExists: "An account already exists with the same email address."
  },
  language: {
    switchTo: "Switch language",
    english: "English",
    croatian: "Croatian",
    german: "German",
    french: "French"
  },
  pdf: {
    pageOf: "Page {{current}} of {{total}}",
    sanctionsCheckHeader: "Sanctions Check Results",
    verified: "VERIFIED",
    flagged: "FLAGGED",
    disclaimer: "DISCLAIMER: This report is for informational purposes only and does not constitute legal advice. The information contained in this report is accurate as of the date of generation.",
    confidential: "CONFIDENTIAL - FOR AUTHORIZED USE ONLY"
  },
  history: {
    history: "History",
    viewHistory: "View Search History",
    searchHistory: "Search History",
    yourRecentSearches: "Your recent searches across all sanctions lists",
    loading: "Loading your search history...",
    loadMore: "Load More",
    noSearchesYet: "No searches yet",
    searchesToAppearHere: "Your searches will appear here after you search for a name",
    recordsScanned: "Records Scanned",
    matchesFound: "Matches Found",
    verificationId: "Verification ID",
    sourcesChecked: "Sources Checked",
    backToHistory: "Back to history",
    searchDetails: "Search Details",
    matchedEntities: "Matched Entities",
    rerunSearch: "Run New Search",
    runningSearch: "Running Search...",
    rerunResults: "New Search Results",
    matches: "Matches",
    moreMatches: "more matches",
    noMatches: "No matches found",
    rerunError: "Error running search",
    filterSearches: "Filter your search history...",
    searching: "Searching...",
    noMatchingSearches: "No searches found matching your filter"
  },
  sidebar: {
    navigation: "Navigation",
    home: "Home",
    searchHistory: "Search History",
    admin: "Admin",
    adminRole: "Administrator",
    userRole: "User",
    massUpload: "Mass Upload",
    monitoring: "Monitoring",
    cases: "Cases"
  },
  massUpload: {
    title: "Mass Screening",
    description: "Upload and screen multiple names at once against sanctions lists",
    selectFile: "Select CSV file or drag and drop",
    loadingJobs: "Loading jobs...",
    loadingResults: "Loading consolidated results...",
    loadingJobDetails: "Loading job details...",
    loadMore: "Load More Jobs",
    loadingMore: "Loading More Jobs...",
    noJobs: "No screening jobs found",
    startFirstJob: "Start your first screening job",
    jobId: "Job ID",
    startScreening: "Start Screening",
    resume: "Resume Screening",
    processing: "Processing...",
    pending: "Pending",
    completed: "Completed",
    foundMatches: "Matches Found",
    error: "Error",
    progress: "Progress",
    summaryTitle: "Screening Results Summary",
    totalProcessed: "Total Processed",
    namesWithMatches: "Names with Matches",
    namesWithoutMatches: "Names without Matches",
    totalMatchesFound: "Total Matches Found",
    clearAndNew: "Clear & New",
    emptyStateMessage: "Upload a file with names to begin batch screening",
    uploading: "Uploading file...",
    namesLoaded: "{{count}} names loaded",
    name: "Name",
    status: "Status",
    matches: "Matches",
    verificationId: "Verification ID",
    actions: "Actions",
    viewMatches: "View Matches",
    hideMatches: "Hide Matches",
    matchDetails: "Match Details",
    sanctionName: "Sanction Name",
    matchPercentage: "Match %",
    confidenceLevel: "Confidence",
    sources: "Sources",
    noDetailedMatches: "No detailed match information available",
    noSourceInfo: "No source information",
    downloadPdf: "Download PDF",
    viewFullReport: "View Full Report",
    jobsTitle: "Screening Jobs",
    newJob: "New Job",
    startTime: "Start Time",
    namesCount: "Names",
    matchesFound: "Matches",
    view: "View",
    viewJob: "View Job Details",
    backToJobs: "Back to Jobs",
    currentJob: "Current Job",
    downloadResults: "Download Results as CSV",
    downloadSuccess: "Results downloaded successfully",
    continued: "Continued",
    completedWithErrors: "Completed with Errors",
    parentJobTitle: "Parent Job Information",
    showChildJobs: "Show Batches",
    hideChildJobs: "Hide Batches",
    totalBatches: "Total Batches",
    totalNames: "Total Names",
    highConfidenceMatches: "High Confidence Matches",
    continuationChain: "Continuation Chain",
    continuationExplanation: "This job is part of a series of linked screening batches. All jobs in the chain are shown below with their current status.",
    jobsInChain: "jobs in this chain",
    continuationInfo: "Continuation Info",
    chainedJobs: "{{count}} jobs in chain",
    parentJob: "Parent Job",
    jobType: "Job Type",
    standardJob: "Standard Job",
    jobPart: "Job Part",
    cancelJob: "Cancel Job",
    cancelling: "Cancelling...",
    stopping: "Stopping",
    stopped: "Stopped",
    jobCancellationRequested: "Job cancellation requested. The job will stop after completing the current batch.",
    couldNotCancelJob: "Could not cancel job",
    failedToCancelJob: "Failed to cancel job",
    fileInfo: "Upload a CSV or TXT file with one name per line",
    batchSizeLimit: "Maximum 250 names per batch",
    oneJobLimit: "Only one active batch job allowed at a time",
    limitReached: "Monthly search limit reached",
    batchSizeLimitError: "Batch size limit exceeded: Your file contains {{count}} names, but the maximum allowed is 250. Please split your data into smaller batches or reduce the number of names in your file.",
    activeJobError: "You already have an active batch job. Please wait for it to complete before starting a new one.",
    errorProcessingFile: "Error Processing File",
    downloadSample: "Download Sample CSV"
  },
  
  // Source translations
  sources: {
    ofac: "OFAC",
    un: "United Nations",
    eu: "European Union",
    uk: "United Kingdom",
    interpol: "Interpol",
    fbi: "FBI",
    pep: "PEP",
    global: "Global Sanctions",
    treasury: "US Treasury",
    unsc: "UN Security Council",
    ustreas: "US Treasury",
    ustreasury: "US Treasury",
    unscr: "UN Security Council Resolution",
    worldbank: "World Bank",
    ecb: "European Central Bank",
    imf: "IMF",
    fsf: "Financial Stability Forum",
    sanctionslist: "Sanctions List",
    usstate: "US State Department",
    usdept: "US Department",
    canada: "Canada",
    australia: "Australia",
    japan: "Japan",
    osfi: "OSFI Canada",
    bis: "Bureau of Industry and Security",
    sdnlist: "SDN List",
    uktreasury: "UK Treasury",
    eucommission: "EU Commission",
    nsa: "National Security Agency",
    nationalsecurity: "National Security",
    globalsanctions: "Global Sanctions",
    sanctionsprogram: "Sanctions Program",
    debarredparties: "Debarred Parties",
    deniedpersons: "Denied Persons",
    entitylist: "Entity List"
  },
  
  monitoring: {
    addedSuccess: "Added to monitoring successfully",
    addError: "Failed to add to monitoring",
    removedSuccess: "Removed from monitoring successfully",
    removeError: "Failed to remove from monitoring",
    missingVerificationId: "Missing verification ID for monitoring",
    alreadyMonitored: "This entity is already being monitored",
    notMonitored: "This entity is not being monitored",
    title: "Monitoring",
    description: "Track entities and automatically re-screen on a regular basis",
    emptyMessage: "Add names to monitoring from the search results page",
    noMonitoredSearches: "No monitored entities",
    totalMonitored: "Total Monitored",
    verificationId: "Verification ID",
    addedOn: "Added On",
    lastChecked: "Last Checked",
    runSearch: "Run Search",
    removeFromMonitoring: "Remove",
    fetchError: "Failed to load monitored entities",
    searchError: "Error occurred while searching",
    errorTitle: "Error Loading Monitoring",
    indexBuildingError: "The search index is currently building. Please try again in a minute.",
    searchCadenceLabel: "Automatic Re-screening Frequency",
    cadenceDescription: "Choose how often we should automatically re-screen these names against the latest sanctions data",
    cadence3Months: "Every 3 months",
    cadence6Months: "Every 6 months",
    cadence9Months: "Every 9 months",
    cadence1Year: "Once a year",
    cadence2Years: "Every 2 years",
    cadenceSaved: "Re-screening frequency saved",
    saveCadenceError: "Failed to save re-screening frequency",
    filterMonitored: "Filter monitored entities...",
    searching: "Searching...",
    noMatchingSearches: "No monitored entities found matching your filter"
  },

  // Add a new section for cases
  cases: {
    title: "Case Management",
    loading: "Loading cases...",
    errorFetching: "Error loading cases",
    noCases: "No cases found",
    totalCases: "Total Cases",
    openCases: "Open Cases",
    allOpenCases: "All Open",
    inProgressCases: "In Progress",
    completedCases: "Completed",
    matches: "Matches",
    reviewed: "Reviewed",
    minute: "minute",
    minutes: "minutes",
    hour: "hour",
    hours: "hours",
    day: "day",
    days: "days",
    ago: "ago",
    statusPending: "Pending",
    statusInProgress: "In Progress",
    statusCompleted: "Completed",
    refresh: "Refresh",
    filters: {
      all: "All",
      allOpen: "All Open",
      pending: "Pending",
      inProgress: "In Progress",
      completed: "Completed"
    },
    report: {
      downloadButton: "Download Report",
      tooltip: "The case report can only be downloaded once all potential matches have been reviewed.",
      loading: "Generating report...",
      error: "Error generating report",
      caseCompleted: "Case Complete",
      casePending: "Case must be fully reviewed to download report",
      matchesConfirmed: "Sanctions Matches Confirmed",
      noMatchesConfirmed: "No Sanctions Matches",
      reviewedBy: "Reviewed by",
      caseSummary: "Case Summary",
      confirmedMatches: "confirmed matches",
      allMatchesCleared: "All matches cleared",
      statusUnknown: "Status unknown"
    }
  },
  cookies: {
    accept: "Accept",
    deny: "Decline",
    consent: "This website uses cookies to ensure you get the best experience. By using this site, you agree to our cookie policy.",
    privacyPolicy: "Privacy Policy",
  },
  pastdue: {
    banner: "Your payment is {{days}} days overdue. Please update your payment information.",
    resolve: "Update Payment Method",
    modal: {
      title: "Payment Overdue",
      message: "Your subscription payment is overdue. You cannot use the app until your payment is resolved.",
      button: "Update Payment Method"
    }
  },
  
  // Trial related messages
  trial: {
    banner: "Your free trial expires in {{days}} days. Upgrade to continue using all features.",
    daysRemaining: "{{days}} days remaining",
    status: "TRIAL",
    upgrade: "Upgrade Now",
    activeTitle: "Free Trial Active",
    description: "You're currently enjoying full access to all Professional features during your free trial period.",
    upgradePrompt: "Upgrade to a paid plan before your trial ends to continue using all features.",
    expired: {
      title: "Your Trial Has Expired",
      message: "Your free trial has ended. Choose a plan below to continue using SanctionsScreener.",
      subtitle: "Select a plan to regain access to all features"
    }
  },

  pricing: {
    choose_plan: "Choose Your Plan",
    upgrade_plan: "Upgrade Your Plan",
    restart_subscription: "Restart Your Subscription",
    subtitle: "Unlock the full power of sanctions screening",
    monthly: "Monthly",
    yearly: "Yearly",
    billed_annually: "billed annually",
    save: "Save",
    most_popular: "Most Popular",
    recommended: "Recommended",
    
    starter: "Starter",
    professional: "Professional",
    enterprise: "Enterprise",
    custom: "Custom",
    
    searches_100: "100 searches per month",
    searches_500: "500 searches per month",
      searches_1000: "Up to 100 searches per month",
  searches_10000: "Up to 1,000 searches per month",
    unlimited_searches: "Unlimited searches",
    
    basic_support: "Basic customer support",
    priority_support: "Priority customer support",
    dedicated_support: "Dedicated account manager",
    
    email_notifications: "Email notifications",
    advanced_notifications: "Advanced notifications",
    
    basic_dashboard: "Basic dashboard",
    detailed_reports: "Detailed reports",
    
    api_access: "API access",
    rest_api_access: "REST API Access",
    bulk_screening: "Bulk screening",
    batch_screening: "Batch screening",
    team_members_3: "3 team members",
    team_members_10: "10 team members",
    active_monitoring: "Active Monitoring",
    case_management: "Case Management",
    custom_integrations: "Custom integrations",
    white_label: "White-label solution",
    sla: "SLA guarantee",
    on_premise: "On-premise deployment option",
    
    get_started: "Get Started",
    upgrade_now: "Upgrade Now",
    contact_sales: "Contact Sales"
  }
};

export default en;
